import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { Box, Fade, Paper } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteCourseSection,
  updateCourseSection,
  deleteCourseActivity
} from "../../redux/actions/contentCoursesActions";
import { activityTypes, defaultShortCourseTitle } from "../../constants/index";
import EditableTitle from "../EditableTitle/EditableTitle";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import ShortCourseActivities from "../CourseBuilder/ShortCourseActivities";
import { reorder, setItemsPosition } from "../../utils/tableUtils";
import { sortBy } from "lodash";
import { useParams } from "react-router-dom";
import { push } from "connected-react-router";
import { buildAddNewPolicyDocumentRoute } from "../../routes/AdminRoutes";
import { usePolicyDocuments } from "../../hooks/policyDocuments";
import usePolicyActivities from "../../hooks/policyActivities";
import { enqueueErrorSnackbar } from "../../redux/actions/snackbarActions";

const policyActivities = (t, acknowledgementExists) => {
  const activitiesToAdd = [
    { label: t("Policy"), value: activityTypes.policyDocument }
  ];

  if (!acknowledgementExists) {
    activitiesToAdd.push({
      label: t("Acknowledgement"),
      value: activityTypes.acknowledgement
    });
  }
  return activitiesToAdd;
};

const getCourseSectionTitle = (t, courseSection) => {
  if (courseSection?.title !== t(defaultShortCourseTitle)) {
    return courseSection.title;
  }
  return "";
};

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    position: "relative",
    flexDirection: "column",
    padding: theme.spacing(4, 0),
    minHeight: 185,
    background: theme.palette.common.white,
    marginBottom: theme.spacing(3)
  },
  isDragging: {
    background: theme.palette.secondary[50]
  }
}));

const CourseSectionDragHandle = ({ dragHandle, show }) => (
  <Fade in={show} data-qa="short-course-drag-handle-fade">
    <Box position="absolute" left={10}>
      {dragHandle}
    </Box>
  </Fade>
);

const ShortCourse = ({
  isDraggingIcon,
  courseSection,
  dragHandle,
  isDraggingOver,
  isDraggingSection,
  showLoadingSpinner,
  onShortCourseTitleUpdate,
  onAddActivityClick,
  onEditActivityClick,
  deleteShortCourse,
  acknowledgementExists,
  canManageContentForPolicy
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { versionId, policyId } = useParams();
  const [hoverOverCourseSection, setHoverOverCourseSection] = useState(false);
  const [currentActivities, setCurrentActivities] = useState([]);

  const {
    createPolicyDocument,
    isCreating: isCreatingPolicyDocument
  } = usePolicyDocuments();
  const { updatePolicyActivities } = usePolicyActivities();
  const { isUpdatingCourseSection } = useSelector(
    ({ contentCoursesState }) => ({
      isUpdatingCourseSection: contentCoursesState.isUpdatingCourseSection
    })
  );

  useEffect(() => {
    if (courseSection && !isUpdatingCourseSection) {
      const courseActivities = sortBy(courseSection.shortCourseActivities, [
        "position"
      ]);
      setCurrentActivities([...courseActivities]);
    }
  }, [courseSection]);

  const handleHoverOverCourseSection = hovering => {
    setHoverOverCourseSection(hovering);
  };

  const handleDeleteCourseSection = () => {
    dispatch(deleteCourseSection(versionId, `${courseSection.id}`));
  };

  const handleTitleChange = title => {
    dispatch(
      updateCourseSection(versionId, courseSection.id, {
        ...courseSection,
        shortCourseActivities: sortBy(courseSection.shortCourseActivities, [
          "position"
        ]),
        title
      })
    );
  };

  const handleReorderActivities = reorderedActivities => {
    // dropped outside the list
    if (!reorderedActivities.destination) {
      return;
    }
    if (
      reorderedActivities.source.index === reorderedActivities.destination.index
    ) {
      return;
    }
    let reordered = reorder(
      currentActivities,
      reorderedActivities.source.index,
      reorderedActivities.destination.index
    );
    reordered = setItemsPosition(reordered, "position");
    setCurrentActivities(reordered);
    dispatch(
      updateCourseSection(versionId, courseSection.id, {
        ...courseSection,
        shortCourseActivities: reordered
      })
    );
  };

  const handleGoToPolicyEditor = policyDocumentId => {
    dispatch(
      push(
        buildAddNewPolicyDocumentRoute(policyId, versionId, policyDocumentId)
      )
    );
  };

  const handleAddPolicyDocument = () => {
    createPolicyDocument({
      itemName: "Policy",
      courseSection: { type: "courseSection", id: courseSection.id }
    })
      .then(({ id: policyDocumentId }) => {
        handleGoToPolicyEditor(policyDocumentId);
      })
      .catch(
        errors =>
          errors[0]?.detail &&
          dispatch(enqueueErrorSnackbar({ message: errors[0].detail }))
      );
  };

  const handleAddActivityClick = activityType => {
    switch (activityType) {
      case activityTypes.policyDocument:
        handleAddPolicyDocument();
        break;
      case activityTypes.acknowledgement:
        onAddActivityClick(activityType, courseSection.id);
    }
  };

  const handleDeleteActivity = activityId => {
    dispatch(deleteCourseActivity(versionId, activityId, courseSection.id));
  };

  const handleEditActivity = activity => {
    switch (activity.contentableType) {
      case activityTypes.policyDocument:
        updatePolicyActivities(activity.itemId, {
          courseSectionId: courseSection.id
        }).then(item => {
          handleGoToPolicyEditor(item.extendableId);
        });
        break;
      case activityTypes.acknowledgement:
        onEditActivityClick(
          activity.contentableType,
          activity.contentableId,
          courseSection.id
        );
    }
  };

  // If true, we don't show the delete course section button
  const hasActivities = courseSection?.shortCourseActivities?.length > 0;

  return (
    <Paper
      className={clsx(classes.root, {
        [classes.isDragging]: isDraggingSection
      })}
    >
      {showLoadingSpinner ? (
        <LoadingSpinner padding={0} containerHeight="100%" size={60} />
      ) : (
        <>
          <Box
            pb={2}
            px={5}
            onMouseEnter={() => handleHoverOverCourseSection(true)}
            onMouseLeave={() => handleHoverOverCourseSection(false)}
            data-qa="policy-builder-short-course"
          >
            <CourseSectionDragHandle
              dragHandle={dragHandle}
              show={
                hoverOverCourseSection &&
                !isDraggingOver &&
                canManageContentForPolicy
              }
            />
            <EditableTitle
              itemId={courseSection ? courseSection.id : null}
              title={getCourseSectionTitle(t, courseSection)}
              showIcons={
                hoverOverCourseSection &&
                !isDraggingOver &&
                canManageContentForPolicy
              }
              onTitleChange={handleTitleChange}
              onAddButtonClick={itemType => handleAddActivityClick(itemType)}
              menuItemsForAddButton={policyActivities(t, acknowledgementExists)}
              onDeleteButtonClick={!hasActivities && handleDeleteCourseSection}
              placeholder={t(defaultShortCourseTitle)}
              addButtonDisabled={isCreatingPolicyDocument}
            />
          </Box>
          <ShortCourseActivities
            activities={currentActivities}
            onReorderActivities={handleReorderActivities}
            onDeleteActivity={handleDeleteActivity}
            onEditActivity={handleEditActivity}
            isOverview={false}
            canManageContentForPolicy={canManageContentForPolicy}
            isPolicy={true}
          />
        </>
      )}
    </Paper>
  );
};

export default ShortCourse;

ShortCourse.propTypes = {
  /**
   * True if the user is dragging an Activity Type icon. This is used to
   * highlight the boxes in the short courses when they are dragging an icon
   */
  isDraggingIcon: PropTypes.bool,
  courseSection: PropTypes.shape({
    // "courseSection"
    type: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    position: PropTypes.number,
    sectionType: PropTypes.string,
    activities: PropTypes.arrayOf(
      PropTypes.shape({
        // "shortCourseActivity"
        type: PropTypes.string,
        id: PropTypes.string,
        name: PropTypes.string,
        contentableType: PropTypes.string,
        position: PropTypes.number
      })
    )
  }),
  /**
   * React element that contains the drag handle icon used to drag and drop the short course
   */
  dragHandle: PropTypes.element,
  /**
   * True if the user is dragging another short course over this short course
   */
  isDraggingOver: PropTypes.bool,
  /**
   * The ID for the course version that this course section belongs to
   */
  versionId: PropTypes.string,
  /**
   * True if the course section is currently being dragged by the user... used for styling
   */
  isDraggingSection: PropTypes.bool,
  /**
   * If true, renders a blank short course with a loading spinner in it
   */
  showLoadingSpinner: PropTypes.bool,
  onAddActivityClick: PropTypes.func,
  onEditActivityClick: PropTypes.func
};
